import React from 'react';
import _ from 'lodash';

import {
  Button,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  styled,
  Switch
} from '@mui/material';
import {
  fields,
  Form,
  formUtil
} from 'iron-forge-react-modules';
import {
  BaseWidget,
  PermissionWall
} from 'modules';
import {
  PermissionName,
  VisibilityScopeName
} from 'generated/graphql';

import { Add } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import * as dataService from './dataService';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();

const FormGrid = styled(Grid)(() => ({
  margin: 8,
  marginLeft: 0
}));


const __permissionsToConfirmChange: PermissionName[] = [
  'platform_godMode',
  'permission_assignRole',
  'permission_updateRole',
  'permission_viewRole'
];



export const FC: React.FC = () => {
  const [selectedRoleId, setSelectedRoleId] = React.useState('');
  const [showEnabledPermissions, setShowEnabledPermissions] = React.useState(false);
  const form = useForm<types.IFormState>({
    defaultValues: {
      displayName: '',
      description: ''
    }
  });
  const queryTuple = dataService.useQuery();
  const [saveUserRoleTemplate, saveUserRoleTemplateTuple] = dataService.useSaveUserRoleTemplate();
  const [deleteUserRoleTemplate, deleteUserRoleTemplateTuple] = dataService.useDeleteUseRoleTemplate();

  const selectedRole = React.useMemo(
    () => _.find(queryTuple.data.roles, role => role.id === selectedRoleId),
    [
      queryTuple.data.roles,
      selectedRoleId
    ]
  );

  const tupleAsFormState = React.useMemo(
    () => ({
      called: queryTuple.called,
      loading: queryTuple.loading,
      data: {
        formState: {
          displayName: selectedRole?.displayName ?? '',
          description: selectedRole?.description ?? ''
        }
      }
    }),
    [queryTuple, selectedRole]
  );

  formUtil.useSetFormFields(
    tupleAsFormState,
    form.setValue,
    [
      'displayName',
      'description'
    ]
  );

  const permission2IsSelectedForRole = React.useMemo(
    () => {
      const permissionName2Permission = _.keyBy(_.flatMap(queryTuple.data.groups, g => g.permissions), p => p.name);
      return _.mapValues(permissionName2Permission, groupPerm =>
        !_.isNil(selectedRole) &&
        !_.isNil(_.find(selectedRole?.permissions ?? [], rolePerm => rolePerm.name === groupPerm.name))
      );
    },
    [
      queryTuple.data.groups,
      selectedRole
    ]
  );

  const groupName2HasEnabledPermissions = React.useMemo(
    () => {
      const groupName2Group = _.keyBy(queryTuple.data.groups, group => group.name);
      const groupName2HasActivePermission = _.mapValues(
        groupName2Group,
        group => _.some(group.permissions, perm => permission2IsSelectedForRole[perm.name])
      );

      return groupName2HasActivePermission;
    },
    [permission2IsSelectedForRole, queryTuple.data.groups]
  );



  React.useEffect(
    () => {
      if (!selectedRoleId && !_.isEmpty(queryTuple.data.roles)) {
        setSelectedRoleId(_.first(queryTuple.data.roles)?.id ?? '');
      }
    },
    [
      queryTuple.data.roles,
      selectedRoleId
    ]
  );



  const rolesByVisibilityScope = React.useMemo(
    () => {
      return _.groupBy(queryTuple.data.roles, role => role.visibilityScopeName);
    },
    [queryTuple.data.roles]
  );

  const groupsToShow = React.useMemo(
    () => showEnabledPermissions
      ? _.filter(queryTuple.data.groups, g => groupName2HasEnabledPermissions[g.name])
      : queryTuple.data.groups,
    [
      groupName2HasEnabledPermissions,
      queryTuple.data.groups,
      showEnabledPermissions
    ]
  );

  const handleClickCreateRole = React.useCallback(
    (visibilityScopeName: VisibilityScopeName) => {
      const displayName = _.trim(window.prompt("Enter the name for the role") ?? '');
      if (displayName) {
        saveUserRoleTemplate({
          displayName,
          visibilityScopeName,
          description: ""
        });
      }
    },
    [saveUserRoleTemplate]
  );

  const processSubmit = React.useCallback(
    (formState: types.IFormState) => {
      if (selectedRoleId) {
        saveUserRoleTemplate(formState, selectedRoleId);
      }
    },
    [
      saveUserRoleTemplate,
      selectedRoleId
    ]
  );

  const handleTogglePermission = React.useCallback(
    (permissionName: PermissionName) => {
      const selectedPermissions = _.keys(_.pickBy(permission2IsSelectedForRole, perm => perm)) as PermissionName[];
      if (_.includes(selectedPermissions, permissionName)) {
        saveUserRoleTemplate(undefined, selectedRoleId, _.filter(selectedPermissions, p => p !== permissionName));

      } else {
        saveUserRoleTemplate(undefined, selectedRoleId, [
          ...selectedPermissions,
          permissionName
        ]);
      }
    },
    [
      permission2IsSelectedForRole,
      saveUserRoleTemplate,
      selectedRoleId
    ]
  );

  const handleClickDelete = React.useCallback(
    async () => {
      if (window.confirm("Are you sure you want to delete this role? This action is irreversible")) {
        await deleteUserRoleTemplate(selectedRoleId);
        setSelectedRoleId(_.first(queryTuple.data.roles)?.id ?? '');
      }
    },
    [
      deleteUserRoleTemplate,
      queryTuple.data.roles,
      selectedRoleId
    ]
  );



  const loading = queryTuple.loading || saveUserRoleTemplateTuple.loading || deleteUserRoleTemplateTuple.loading;

  return (
    <BaseWidget.FC>
      <Grid
        container
        spacing={3}>
        <Grid
          item
          xs={4}>
          <List
            subheader={<li />}
            sx={{
              bgcolor: 'background.paper',
              '& ul': { padding: 0 }
            }}>
            {_.map(rolesByVisibilityScope, (roles, key) => (
              <li key={`section-${key}`}>
                <ul>
                  {key !== 'global' && (<Divider />)}

                  <ListSubheader>
                    <Stack
                      direction="row"
                      justifyContent="space-between">
                      {_.first(roles)?.visibilityScopeDisplayName}

                      <PermissionWall.FC requiresPermissions={[
                        'permission_updateRole'
                      ]}>
                        <Button
                          onClick={() => handleClickCreateRole(key as VisibilityScopeName)}
                          startIcon={<Add />}>
                          Create {_.first(roles)?.visibilityScopeDisplayName} Role
                        </Button>
                      </PermissionWall.FC>
                    </Stack>
                  </ListSubheader>

                  {_.map(roles, role => (
                    <ListItem key={role.id}>
                      <ListItemButton
                        selected={role.id === selectedRoleId}
                        onClick={_.partial(setSelectedRoleId, role.id)}>
                        <ListItemText>
                          {role.displayName}
                        </ListItemText>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </ul>
              </li>
            ))}

          </List>
        </Grid>

        <Grid
          item
          xs={8}>
          <PermissionWall.FC requiresPermissions={['permission_updateRole']}>
            <Form.FC onSubmit={form.handleSubmit(processSubmit)}>
              <FormGrid
                container
                spacing={2}>
                <Grid
                  item
                  xs={4}>
                  <ControlledTextField
                    isDisabled={loading}
                    formControl={form.control}
                    fieldKey="displayName"
                    label="Display Name"
                    isRequired />
                </Grid>

                <Grid
                  item
                  xs={4}>
                  <ControlledTextField
                    isDisabled={loading}
                    formControl={form.control}
                    fieldKey="description"
                    label="Description" />
                </Grid>

                <Grid
                  item
                  xs={4}>
                  <Button
                    disabled={loading}
                    variant="contained"
                    type="submit">
                    Save
                  </Button>

                  <Button
                    disabled={loading}
                    color="error"
                    onClick={handleClickDelete}>
                    Delete
                  </Button>
                </Grid>
              </FormGrid>
            </Form.FC>
          </PermissionWall.FC>

          <FormControlLabel
            label="Show Enabled"
            control={(
              <Switch
                checked={showEnabledPermissions}
                onChange={() => setShowEnabledPermissions(!showEnabledPermissions)} />
            )} />

          <List
            sx={{
              bgcolor: 'background.paper',
              '& ul': { padding: 0 }
            }}
            subheader={<li />}>
            {_.map(groupsToShow, group => (
              <li key={`section-${group.name}`}>
                <ul>
                  <Divider />
                  <ListSubheader>{group.displayName}</ListSubheader>

                  {_.map(_.filter(group.permissions, p => showEnabledPermissions && permission2IsSelectedForRole[p.name] || !showEnabledPermissions), perm => (
                    <ListItem key={`item-${group.name}-${perm.name}`}>
                      <ListItemText primary={perm.displayName} />

                      <Switch
                        disabled={loading}
                        checked={permission2IsSelectedForRole[perm.name]}
                        onChange={() => {
                          if (
                            _.includes(__permissionsToConfirmChange, perm.name) &&
                            !window.confirm("Are you sure you want to modify this permission? This may affect how you can change permissions on the platform.")
                          ) {
                            return;
                          }

                          handleTogglePermission(perm.name);
                        }} />
                    </ListItem>
                  ))}
                </ul>
              </li>
            ))}
          </List>
        </Grid>
      </Grid>
    </BaseWidget.FC>
  );
};
